/**
 * The pane's icon set, under the names the code already uses.
 *
 * The web app draws its own chrome with Streamline icons at a 1px stroke
 * (`mytender-frontend/src/components/icons`); the pane used lucide at its
 * 2px default, which is why the two looked unrelated side by side (Jamie,
 * #1297). Every icon the pane chrome uses is exported here once: the ones
 * with a Streamline equivalent are that drawing, wrapped to take lucide's
 * `size` and `className`; the rest stay lucide at a 1px stroke until they
 * are drawn. Swapping one is a one-line change in this file. The chat
 * components copied from the web app keep their lucide imports, as the web
 * app's own chat does.
 */
import type { LucideIcon, LucideProps } from "lucide-react";
import {
  ArchiveIcon as LucideArchive,
  ArrowRightIcon as LucideArrowRight,
  ArrowUpRightIcon as LucideArrowUpRight,
  ChevronDownIcon as LucideChevronDown,
  ChevronRightIcon as LucideChevronRight,
  FilePlus2Icon as LucideFilePlus2,
  Heading2Icon as LucideHeading2,
  Heading3Icon as LucideHeading3,
  PaintbrushIcon as LucidePaintbrush,
  XIcon as LucideX,
  ArrowLeftRightIcon as LucideArrowLeftRight,
  ExternalLinkIcon as LucideExternalLink,
  UnlinkIcon as LucideUnlink,
} from "lucide-react";
import { type FC, forwardRef } from "react";

import StreamlineAiEditSpark from "./streamline/AiEditSparkIcon";
import StreamlineArrowLeft from "./streamline/ArrowLeftIcon";
import StreamlineBin from "./streamline/BinIcon";
import StreamlineBold from "./streamline/BoldIcon";
import StreamlineBullets from "./streamline/BulletsIcon";
import StreamlineCheck from "./streamline/CheckIcon";
import StreamlineContentLibrary from "./streamline/ContentLibraryIcon";
import StreamlineDoc from "./streamline/DocIcon";
import StreamlineElipsisMenu from "./streamline/ElipsisMenuIcon";
import StreamlineFile from "./streamline/FileIcon";
import StreamlineItalic from "./streamline/ItalicIcon";
import StreamlineLogout from "./streamline/LogoutIcon";
import StreamlinePencil from "./streamline/PencilIcon";
import StreamlinePlus from "./streamline/PlusIcon";
import StreamlineSearch from "./streamline/SearchIcon";
import StreamlineTextFormatNumeric from "./streamline/TextFormatNumericIcon";
import StreamlineUpload from "./streamline/UploadIcon";
import type { IconProps } from "./streamline/types";

/** What an icon slot accepts: lucide's props (size, className, strokeWidth is ignored on Streamline drawings). */
export type IconComponent = FC<LucideProps>;

const DEFAULT_SIZE = 16;
const DEFAULT_STROKE = 1;

/** A Streamline drawing behind lucide's props. */
const streamline = (Drawing: FC<IconProps>, name: string): IconComponent => {
  // eslint-disable-next-line react/prop-types -- typed through LucideProps
  const Icon: IconComponent = ({ size = DEFAULT_SIZE, className }) => (
    <Drawing width={size} height={size} className={className} />
  );
  Icon.displayName = name;
  return Icon;
};

/** A lucide drawing at the web app's stroke weight, until a Streamline one exists. */
const thin = (Drawing: LucideIcon, name: string): IconComponent => {
  const Icon = forwardRef<SVGSVGElement, LucideProps>(({ strokeWidth = DEFAULT_STROKE, ...props }, ref) => (
    <Drawing ref={ref} strokeWidth={strokeWidth} {...props} />
  ));
  Icon.displayName = name;
  return Icon as unknown as IconComponent;
};

export const ArrowLeftIcon = streamline(StreamlineArrowLeft, "ArrowLeftIcon");
export const CheckIcon = streamline(StreamlineCheck, "CheckIcon");
export const SearchIcon = streamline(StreamlineSearch, "SearchIcon");
export const UploadIcon = streamline(StreamlineUpload, "UploadIcon");
export const PlusIcon = streamline(StreamlinePlus, "PlusIcon");
export const PencilIcon = streamline(StreamlinePencil, "PencilIcon");
export const BoldIcon = streamline(StreamlineBold, "BoldIcon");
export const ItalicIcon = streamline(StreamlineItalic, "ItalicIcon");
export const FileIcon = streamline(StreamlineFile, "FileIcon");
export const FileTextIcon = streamline(StreamlineDoc, "FileTextIcon");
export const TrashIcon = streamline(StreamlineBin, "TrashIcon");
export const MoreHorizontalIcon = streamline(StreamlineElipsisMenu, "MoreHorizontalIcon");
export const ListIcon = streamline(StreamlineBullets, "ListIcon");
export const ListOrderedIcon = streamline(StreamlineTextFormatNumeric, "ListOrderedIcon");
export const DatabaseIcon = streamline(StreamlineContentLibrary, "DatabaseIcon");
export const SparklesIcon = streamline(StreamlineAiEditSpark, "SparklesIcon");
export const LogOutIcon = streamline(StreamlineLogout, "LogOutIcon");

// Not in the Streamline set yet: lucide at the same weight.
export const XIcon = thin(LucideX, "XIcon");
export const ChevronRightIcon = thin(LucideChevronRight, "ChevronRightIcon");
export const ChevronDownIcon = thin(LucideChevronDown, "ChevronDownIcon");
export const ArrowRightIcon = thin(LucideArrowRight, "ArrowRightIcon");
export const ArrowUpRightIcon = thin(LucideArrowUpRight, "ArrowUpRightIcon");
export const ArchiveIcon = thin(LucideArchive, "ArchiveIcon");
export const FilePlus2Icon = thin(LucideFilePlus2, "FilePlus2Icon");
export const Heading2Icon = thin(LucideHeading2, "Heading2Icon");
export const Heading3Icon = thin(LucideHeading3, "Heading3Icon");
export const PaintbrushIcon = thin(LucidePaintbrush, "PaintbrushIcon");
export const ArrowLeftRightIcon = thin(LucideArrowLeftRight, "ArrowLeftRightIcon");
export const ExternalLinkIcon = thin(LucideExternalLink, "ExternalLinkIcon");
export const UnlinkIcon = thin(LucideUnlink, "UnlinkIcon");
